// src/pages/settings/DeliveryPartners.jsx
import { useMemo, useState } from "react";
import { Card, CardHeader, CardBody } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Modal } from "../../components/ui/Modal";
import { Input } from "../../components/ui/Input";
import { Badge } from "../../components/ui/Badge";
import { Table, Th, Td } from "../../components/ui/Table";
import { useDeliveryPartners } from "../../hooks/useDeliveryPartners";

export default function DeliveryPartners() {
  const {
    partners,
    loading,
    saving,
    error,
    createPartner,
    updatePartner,
    deletePartner,
  } = useDeliveryPartners();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const initialForm = useMemo(
    () => ({
      name: "",
      api_endpoint: "",
      active: true,
      credentials_ref: "",
    }),
    []
  );

  const [form, setForm] = useState(initialForm);

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      api_endpoint: p.api_endpoint || "",
      active: Boolean(p.active),
      credentials_ref: p.credentials_ref || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return;

    if (editing) {
      await updatePartner(editing.id, form);
    } else {
      await createPartner(form);
    }
    setOpen(false);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Delivery Partners</h1>
          <p className="text-sm text-slate-500 dark:text-slate-500">
            Manage partners + view performance KPIs (success rate, avg delivery time).
          </p>
        </div>

        <Button onClick={openCreate}>+ Add partner</Button>
      </div>

      {error ? (
        <div className="rounded-xl border border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 p-3 text-sm text-rose-700 dark:text-rose-400">
          Failed loading partners.
        </div>
      ) : null}

      <Card>
        <CardHeader title="Partners" subtitle={loading ? "Loading..." : `${partners.length} partners`} />
        <CardBody className="p-0">
          {loading ? (
            <div className="p-4 text-sm text-slate-500 dark:text-slate-500">Loading...</div>
          ) : (
            <div className="overflow-auto">
              <Table>
                <thead className="bg-slate-50 dark:bg-slate-800/60">
                  <tr>
                    <Th>Name</Th>
                    <Th>Endpoint</Th>
                    <Th>KPIs</Th>
                    <Th>Status</Th>
                    <Th className="text-right">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map((p) => (
                    <tr key={p.id} className="border-t dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800">
                      <Td className="font-semibold">{p.name}</Td>
                      <Td className="text-slate-700 dark:text-slate-300">{p.api_endpoint}</Td>
                      <Td>
                        <div className="flex flex-wrap gap-2">
                          <Badge color="green">
                            Success {p.kpis?.success_rate ?? "—"}%
                          </Badge>
                          <Badge color="indigo">
                            Avg {p.kpis?.avg_delivery_time_days ?? "—"} d
                          </Badge>
                        </div>
                      </Td>
                      <Td>
                        {p.active ? (
                          <Badge color="green">Active</Badge>
                        ) : (
                          <Badge>Inactive</Badge>
                        )}
                      </Td>
                      <Td className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEdit(p)}
                            disabled={saving}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => deletePartner(p.id)}
                            disabled={saving}
                          >
                            Delete
                          </Button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        open={open}
        title={editing ? `Edit Partner: ${editing.name}` : "Add Delivery Partner"}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving}>
              {editing ? "Save" : "Create"}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Name</div>
            <Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} />
          </div>

          <div>
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">API Endpoint</div>
            <Input
              value={form.api_endpoint}
              onChange={(e) => setForm((s) => ({ ...s, api_endpoint: e.target.value }))}
              placeholder="https://partner-api.example.com/orders"
            />
          </div>

          <div>
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Credentials Ref</div>
            <Input
              value={form.credentials_ref}
              onChange={(e) => setForm((s) => ({ ...s, credentials_ref: e.target.value }))}
              placeholder="vault:partner-prod"
            />
          </div>

          <label className="flex items-center gap-2 text-sm dark:text-slate-300">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm((s) => ({ ...s, active: e.target.checked }))}
              className="h-4 w-4 accent-indigo-600"
            />
            Active
          </label>
        </div>
      </Modal>
    </div>
  );
}